import { clsx } from 'clsx'
import { ArrowLeft, Users } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAccount } from '@/hooks/use-accounts'
import { formatNumber } from '@/lib/format'
import { AuditTab } from '@/pages/account-detail/audit-tab'
import { BillingTab } from '@/pages/account-detail/billing-tab'
import { DomainsTab } from '@/pages/account-detail/domains-tab'
import { OverridesTab } from '@/pages/account-detail/overrides-tab'
import { OverviewTab } from '@/pages/account-detail/overview-tab'

const TABS = ['overview', 'overrides', 'billing', 'domains', 'audit'] as const
type Tab = (typeof TABS)[number]

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Overview',
  overrides: 'Overrides',
  billing: 'Billing',
  domains: 'Domains',
  audit: 'Audit',
} as const

export function AccountDetailPage(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  const { data: account, isLoading, error } = useAccount(id ?? '')

  if (isLoading) {
    return <LoadingPage message="Loading account..." />
  }

  if (error || !account) {
    return (
      <div className="space-y-6">
        <BackButton onClick={() => navigate('/accounts')} />
        <div className="rounded-xl border border-danger/30 bg-surface-0 p-12 text-center">
          <p className="text-danger">
            Failed to load account. It may not exist or you may lack permission.
          </p>
        </div>
      </div>
    )
  }

  const effectiveLimit = account.overrides?.email_limit ?? getDefaultLimit(account.tier)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <BackButton onClick={() => navigate('/accounts')} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-admin-500" />
            <h1 className="truncate text-xl font-semibold text-text-primary">
              {account.user_email}
            </h1>
          </div>
          <div className="mt-1 flex items-center gap-2">
            {account.user_name && (
              <span className="text-sm text-text-secondary">{account.user_name}</span>
            )}
            <StatusBadge status={account.tier} />
            <StatusBadge status={account.sending_paused ? 'paused' : account.status} />
            {account.overrides && <StatusBadge status="custom" />}
          </div>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Emails Sent"
          value={formatNumber(account.emails_sent_this_period)}
          sub="This period"
        />
        <MetricCard
          label="Email Limit"
          value={formatNumber(effectiveLimit)}
          sub={account.overrides?.email_limit ? 'Custom override' : 'Tier default'}
          variant={account.overrides?.email_limit ? 'warning' : 'default'}
        />
        <MetricCard label="Domains" value={String(account.domain_count)} />
        <MetricCard label="API Keys" value={String(account.api_key_count)} />
      </div>

      {/* Tabs */}
      <div className="border-b border-surface-3">
        <nav className="flex gap-1">
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                'px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-b-2 border-admin-500 text-admin-600'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab account={account} />}
      {activeTab === 'overrides' && <OverridesTab account={account} />}
      {activeTab === 'billing' && <BillingTab account={account} />}
      {activeTab === 'domains' && <DomainsTab account={account} />}
      {activeTab === 'audit' && <AuditTab account={account} />}
    </div>
  )
}

function BackButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 rounded-lg p-1.5 text-text-secondary hover:bg-surface-2"
    >
      <ArrowLeft className="h-5 w-5" />
    </button>
  )
}

function getDefaultLimit(tier: string): number {
  const limits: Record<string, number> = {
    free: 1000,
    starter: 10000,
    pro: 50000,
    growth: 200000,
    scale: 1000000,
  }
  return limits[tier] ?? 1000
}
